import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Mail, Phone, Lock, Eye, EyeOff, X, Key, CheckCircle2, ShieldAlert, ArrowLeft } from 'lucide-react';
import { authService } from '../services/api';
import { SubdomainLink } from '../components/SubdomainLink';
import { useToast } from '../hooks/useToast';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const handleBackClick = () => {
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get('redirect');

    const isAuthPath = (path) => {
      if (!path) return false;
      const lowerPath = path.toLowerCase();
      return lowerPath.startsWith('/admin') || lowerPath.startsWith('/my-account');
    };

    if (redirectParam) {
      const target = decodeURIComponent(redirectParam);
      if (isAuthPath(target)) {
        navigate('/');
      } else {
        navigate(target);
      }
      return;
    }

    if (location.state?.from) {
      const fromPath = typeof location.state.from === 'string'
        ? location.state.from
        : location.state.from.pathname || '';
      
      if (isAuthPath(fromPath)) {
        navigate('/');
        return;
      }
    }

    navigate(-1);
  };
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);

  // OTP Verification States
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [maskedMobile, setMaskedMobile] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    emailId: '',
    mobileNo: '',
    password: ''
  });

  // Track which fields the user has touched (to avoid showing errors before user interacts)
  const [touched, setTouched] = useState({
    name: false,
    emailId: false,
    mobileNo: false,
    password: false
  });

  // --- Validation Rules ---
  const validate = (data) => {
    const errors = {};

    if (!data.name.trim()) {
      errors.name = 'Full name is required.';
    } else if (data.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters.';
    }

    if (!data.mobileNo.trim()) {
      errors.mobileNo = 'Mobile number is required.';
    } else if (!/^\d{10}$/.test(data.mobileNo.trim())) {
      errors.mobileNo = 'Mobile number must be exactly 10 digits.';
    }

    if (!data.emailId.trim()) {
      errors.emailId = 'Email ID is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.emailId.trim())) {
      errors.emailId = 'Please enter a valid email address.';
    }

    if (!data.password) {
      errors.password = 'Password is required.';
    } else if (data.password.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }

    return errors;
  };

  const fieldErrors = validate(formData);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBlur = (e) => {
    setTouched({ ...touched, [e.target.name]: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched on submit attempt
    setTouched({ name: true, emailId: true, mobileNo: true, password: true });

    // Checkbox validation
    if (!agreedToTerms) {
      setTermsError(true);
      if (Object.keys(validate(formData)).length > 0) return;
      return;
    } else {
      setTermsError(false);
    }

    // Block submit if any field-level errors
    if (Object.keys(validate(formData)).length > 0) return;

    setLoading(true);
    setError('');

    const result = await authService.registerRequestOtp(formData.emailId, formData.mobileNo);

    if (result.success) {
      const mobile_no = formData.mobileNo;
      let masked = mobile_no;
      if (mobile_no.length >= 10) {
        masked = mobile_no.slice(0, 6) + "XXXX";
      }
      setMaskedMobile(masked);
      setShowOtpModal(true);
      setOtp('');
      setOtpError('');
      setOtpSuccess('An OTP has been sent to your mobile number.');
    } else {
      setError(String(result.error));
    }
    setLoading(false);
  };

  const handleVerifyOtpAndRegister = async (e) => {
    e.preventDefault();
    if (!/^[0-9]{6}$/.test(otp)) {
      setOtpError('OTP must be exactly 6 digits.');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    setOtpSuccess('');

    const payload = {
      ...formData,
      otp: otp
    };

    const result = await authService.register(payload);

    if (result.success) {
      // Google Tag Manager / Analytics conversion tracking
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'signup_success' });
      } catch (gtmErr) {
        console.error("GTM dataLayer push failed:", gtmErr);
      }

      try {
        const emailInput = document.getElementById('zoho_register_email_val');
        const registerForm = document.getElementById('zoho_register_optin_form');
        if (emailInput && registerForm) {
          emailInput.value = formData.emailId;
          registerForm.submit();
        }
      } catch (err) {
        console.error("Zoho auto-subscription failed:", err);
      }

      setOtpSuccess('Registration successful! Logging in...');

      // Auto login after successful registration
      try {
        const loginResult = await authService.login({
          username: formData.emailId,
          password: formData.password
        });
        if (loginResult.success) {
          toast.success('Registration successful! You are now logged in.');
          setTimeout(() => {
            setShowOtpModal(false);
            navigate('/my-account/manage-listing', { replace: true });
          }, 1500);
          setOtpLoading(false);
          return;
        }
      } catch (loginErr) {
        console.error("Auto login failed:", loginErr);
      }

      // Fallback: If auto-login fails, redirect to regular login page
      toast.success('Registration successful! Please login.');
      setTimeout(() => {
        setShowOtpModal(false);
        navigate('/login', { state: location.state });
      }, 1500);
    } else {
      setOtpError(String(result.error));
    }
    setOtpLoading(false);
  };

  const handleResendOtp = async () => {
    setOtpLoading(true);
    setOtpError('');
    setOtpSuccess('');

    const result = await authService.registerRequestOtp(formData.emailId, formData.mobileNo);

    if (result.success) {
      setOtpSuccess('A new OTP has been sent to your mobile number.');
    } else {
      setOtpError(String(result.error));
    }
    setOtpLoading(false);
  };

  // Helper: returns classes for an input based on validation state
  const inputClass = (fieldName) => {
    const hasError = touched[fieldName] && fieldErrors[fieldName];
    if (hasError) {
      return 'block w-full pl-10 pr-3 py-3 border-2 border-red-400 rounded-lg bg-white focus:ring-2 focus:ring-red-300 focus:outline-none text-red-700';
    }
    return 'block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:outline-none';
  };

  const passwordInputClass = () => {
    const hasError = touched.password && fieldErrors.password;
    if (hasError) {
      return 'block w-full pl-10 pr-12 py-3 border-2 border-red-400 rounded-lg bg-white focus:ring-2 focus:ring-red-300 focus:outline-none text-red-700';
    }
    return 'block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:outline-none';
  };

  const iconClass = (fieldName) => {
    const hasError = touched[fieldName] && fieldErrors[fieldName];
    if (hasError) return 'absolute inset-y-0 left-0 pl-3 flex items-center text-red-400';
    return 'absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400';
  };

  return (
    <div className="min-h-screen flex font-sans">
      {/* Left Side - Green Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A734E] text-white p-20 flex-col justify-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#23855B] rounded-full -mr-48 -mt-48 opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#23855B] rounded-full -ml-40 -mb-40 opacity-50"></div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-5xl font-bold mb-8">Top Interiors India</h1>
          <p className="text-xl leading-relaxed opacity-90 mb-16">
            Join India's largest network of verified vendors and connect with top architects.
          </p>

          <div className="flex space-x-16">
            <div>
              <p className="text-4xl font-bold mb-2">2,500+</p>
              <p className="text-sm uppercase tracking-widest opacity-70">Vendors</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">50+</p>
              <p className="text-sm uppercase tracking-widest opacity-70">Cities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Expanded Form */}
      <div className="w-full lg:w-1/2 bg-[#F9FAF7] flex items-center justify-center p-8 overflow-y-auto">
        <div className="max-w-2xl w-full py-10">
          <div className="mb-6 text-left">
            <button
              type="button"
              onClick={handleBackClick}
              className="inline-flex items-center text-sm font-bold text-[#1A734E] hover:text-[#155D3F] transition-colors group bg-transparent border-0 p-0 cursor-pointer"
            >
              <ArrowLeft size={16} className="mr-1.5 transform group-hover:-translate-x-0.5 transition-transform" />
              Back
            </button>
          </div>
          <div className="text-left mb-8">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Create Account</h2>
            <p className="text-gray-500">Please fill in all details to get started.</p>
          </div>

          {/* noValidate disables browser's default HTML5 tooltip validation */}
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Name <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className={iconClass('name')}><User size={18} /></span>
                <input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass('name')}
                  placeholder="Enter full name"
                />
              </div>
              {touched.name && fieldErrors.name && (
                <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                  {fieldErrors.name}
                </p>
              )}
            </div>

            {/* Mobile No */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Mobile No <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className={iconClass('mobileNo')}><Phone size={18} /></span>
                <input
                  name="mobileNo"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={formData.mobileNo}
                  onChange={(e) => {
                    // Only allow digits
                    const val = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, mobileNo: val });
                  }}
                  onBlur={handleBlur}
                  className={inputClass('mobileNo')}
                  placeholder="1234567890"
                />
              </div>
              {touched.mobileNo && fieldErrors.mobileNo && (
                <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                  {fieldErrors.mobileNo}
                </p>
              )}
            </div>

            {/* Email ID */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Email ID <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className={iconClass('emailId')}><Mail size={18} /></span>
                <input
                  name="emailId"
                  type="email"
                  value={formData.emailId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass('emailId')}
                  placeholder="admin@gmail.com"
                />
              </div>
              {touched.emailId && fieldErrors.emailId && (
                <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                  {fieldErrors.emailId}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className={iconClass('password')}><Lock size={18} /></span>
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={passwordInputClass()}
                  placeholder="Min. 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.password && fieldErrors.password && (
                <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                  {fieldErrors.password}
                </p>
              )}
            </div>

            <div className="space-y-1.5 pt-2">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    if (e.target.checked) setTermsError(false);
                  }}
                  className={`h-4 w-4 mt-0.5 flex-shrink-0 rounded cursor-pointer focus:ring-2 ${termsError
                    ? 'border-red-400 text-red-500 focus:ring-red-300 accent-red-500'
                    : 'border-gray-300 text-primary focus:ring-primary accent-[#1A734E]'
                    }`}
                />
                <label htmlFor="terms-checkbox" className="text-sm text-gray-600 cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms-conditions" target="_blank" rel="noopener noreferrer" className="text-[#1A734E] font-bold hover:underline">Terms & Conditions</Link>{' '}and{' '}
                  <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#1A734E] font-bold hover:underline">Privacy Policy</Link>
                </label>
              </div>
              {termsError && (
                <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                  You must agree to the Terms & Conditions and Privacy Policy.
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-4 px-4 rounded-lg shadow-sm text-lg font-bold text-white bg-[#1A734E] hover:bg-[#155D3F] focus:outline-none transition-all mt-4 disabled:opacity-70"
            >
              {loading ? 'Processing...' : 'Create Account'}
            </button>

            <div className="text-center pt-4">
              <p className="text-gray-500 text-[15px]">
                Already have an account? <Link to="/login" className="text-[#1A734E] font-bold hover:underline">Sign In</Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 transition-all duration-300">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 border border-gray-100 flex flex-col relative animate-in fade-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              onClick={() => setShowOtpModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              title="Close"
            >
              <X size={18} />
            </button>

            <div className="text-left mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Verify Mobile Number</h3>
              <p className="text-sm text-gray-500 mt-1">
                We've sent a 6-digit code to your mobile number: {maskedMobile}.
              </p>
            </div>

            {/* Error & Success Badges */}
            {otpError && (
              <div className="bg-rose-50 text-rose-800 border border-rose-100 p-3.5 rounded-xl text-xs flex items-center gap-2.5 mb-5 font-semibold">
                <ShieldAlert size={16} className="text-rose-500 flex-shrink-0" />
                <span>{otpError}</span>
              </div>
            )}
            {otpSuccess && (
              <div className="bg-emerald-50 text-emerald-800 border border-emerald-100 p-3.5 rounded-xl text-xs flex items-center gap-2.5 mb-5 font-semibold">
                <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                <span>{otpSuccess}</span>
              </div>
            )}

            <form onSubmit={handleVerifyOtpAndRegister} className="space-y-5">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  6-Digit OTP Code <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                    <Key size={18} />
                  </span>
                  <input
                    type="text"
                    maxLength={6}
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1A734E] focus:border-transparent transition-all text-sm tracking-widest font-mono text-center font-bold"
                    placeholder="XXXXXX"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowOtpModal(false)}
                  className="w-1/3 py-3 border border-gray-200 text-gray-600 rounded-lg text-sm font-bold hover:bg-gray-50 active:scale-[0.98] transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={otpLoading}
                  className="w-1/3 py-3 border border-gray-200 text-[#1A734E] rounded-lg text-sm font-bold hover:bg-gray-50 active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  Resend
                </button>
                <button
                  type="submit"
                  disabled={otpLoading}
                  className="w-1/3 py-3 bg-[#1A734E] hover:bg-[#155D3F] text-white rounded-lg text-sm font-bold active:scale-[0.98] transition-all shadow-sm shadow-[#1A734E]/10 flex items-center justify-center disabled:opacity-50"
                >
                  {otpLoading ? "Verifying..." : "Verify"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Hidden Zoho Newsletter subscription form */}
      <form 
        method="POST" 
        id="zoho_register_optin_form" 
        action="https://zgp4-zgp4.maillist-manage.in/weboptin.zc" 
        target="zoho_register_iframe"
        style={{ display: 'none' }}
      >
        <input type="hidden" name="CONTACT_EMAIL" id="zoho_register_email_val" value="" />
        <input type="hidden" name="submitType" value="optinCustomView" />
        <input type="hidden" name="emailReportId" value="" />
        <input type="hidden" name="formType" value="QuickForm" />
        <input type="hidden" name="zx" value="1dfc3cc24e" />
        <input type="hidden" name="zcvers" value="2.0" />
        <input type="hidden" name="oldListIds" value="" />
        <input type="hidden" name="mode" value="OptinCreateView" />
        <input type="hidden" name="zcld" value="14b758dc472e2f8d" />
        <input type="hidden" name="zctd" value="14b758dc472e2e31" />
        <input type="hidden" name="document_domain" value="" />
        <input type="hidden" name="zc_Url" value="zgp4-zgp4.maillist-manage.in" />
        <input type="hidden" name="new_optin_response_in" value="0" />
        <input type="hidden" name="duplicate_optin_response_in" value="0" />
        <input type="hidden" name="zc_trackCode" value="ZCFORMVIEW" />
        <input type="hidden" name="zc_formIx" value="3z6f8371087271fb9407595008a691d5b366f565743b77561afcc30bbab3a07707" />
        <input type="hidden" name="viewFrom" value="URL_ACTION" />
      </form>
      <iframe 
        name="zoho_register_iframe" 
        id="zoho_register_iframe" 
        style={{ display: 'none' }}
        title="Zoho Campaigns Register Subscription Frame"
      ></iframe>
    </div>
  );
};

export default Register;
